import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Filter, Plus, Search } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { useAuth } from '../../contexts/AuthContext';
import type { InventoryItem } from '../../types/inventory';
import { getInventoryItemsManage, setInventoryItemStockTracked } from '../../utils/inventoryApi';

type CatalogTab = 'all' | 'product' | 'medication' | 'immunization' | 'diagnostic' | 'procedure' | 'treatment';

export const InventoryItems: React.FC = () => {
  const { user } = useAuth();
  const practiceId = user?.practice_id;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [items, setItems] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddMenu, setShowAddMenu] = useState(false);
  const addMenuRef = useRef<HTMLDivElement>(null);
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const filterMenuRef = useRef<HTMLDivElement>(null);

  // URL-driven filter (RESTful/back-button friendly):
  // /settings/inventory?tab=inventory-items&items=medication
  const tabFromUrl = (searchParams.get('items') || 'all').toLowerCase().trim() as CatalogTab;
  const activeTab: CatalogTab = ([
    'all',
    'product',
    'medication',
    'immunization',
    'diagnostic',
    'procedure',
    'treatment',
  ] as const).includes(tabFromUrl as any)
    ? tabFromUrl
    : 'all';

  // Filter preference (not URL-driven) — match Billing Catalog approach.
  const [stockTrackedOnly, setStockTrackedOnly] = useState<boolean>(() => {
    return localStorage.getItem('inventory_stockTrackedOnly') === 'true';
  });

  const setStockTrackedOnlyPersisted = (nextValue: boolean) => {
    setStockTrackedOnly(nextValue);
    localStorage.setItem('inventory_stockTrackedOnly', String(nextValue));
  };

  const setActiveTab = (nextTab: CatalogTab) => {
    const next = new URLSearchParams(searchParams);
    if (nextTab === 'all') {
      next.delete('items');
    } else {
      next.set('items', nextTab);
    }
    setSearchParams(next); // push history so Back/Forward works
  };

  const fetchItems = async () => {
    if (!practiceId) return;
    setLoading(true);
    try {
      const data = await getInventoryItemsManage(practiceId, { include_inactive: false });
      setItems(data);
    } catch (err: any) {
      console.error('Failed to fetch inventory items (manage):', err);
      alert(err?.message || 'Failed to fetch inventory items');
    } finally {
      setLoading(false);
    }
  };

  useEscapeKey(() => {
    setShowAddMenu(false);
    setShowFilterMenu(false);
  }, showAddMenu || showFilterMenu);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (showAddMenu && addMenuRef.current && !addMenuRef.current.contains(target)) {
        setShowAddMenu(false);
      }
      if (showFilterMenu && filterMenuRef.current && !filterMenuRef.current.contains(target)) {
        setShowFilterMenu(false);
      }
    };
    // capture phase so it still closes if something stops propagation
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, [showAddMenu, showFilterMenu]);

  useEffect(() => {
    if (!practiceId) return;
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceId]);

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return items.filter((it) => {
      if (activeTab !== 'all' && it.catalog_type !== activeTab) return false;
      if (stockTrackedOnly && !it.is_stock_tracked) return false;
      if (!term) return true;
      return (
        it.name.toLowerCase().includes(term) ||
        (it.sku ? it.sku.toLowerCase().includes(term) : false)
      );
    });
  }, [items, activeTab, searchTerm, stockTrackedOnly]);

  const toggleStockTracked = async (it: InventoryItem, next: boolean) => {
    if (!practiceId) return;
    try {
      const res = await setInventoryItemStockTracked(practiceId, it.catalog_type, it.catalog_item_id, next);

      // Update in-place to avoid "jumping" / re-sorting UX.
      // If we created an override, the id changes — keep the row in place and just swap ids.
      setItems((prev) =>
        prev.map((x) => {
          if (x.catalog_type !== it.catalog_type) return x;
          if (x.catalog_item_id !== it.catalog_item_id) return x;
          return {
            ...x,
            catalog_item_id: res.effective_catalog_item_id,
            is_stock_tracked: res.is_stock_tracked,
          };
        })
      );
    } catch (err: any) {
      console.error('Failed to toggle stock tracking:', err);
      alert(err?.message || 'Failed to update stock tracking');
    }
  };

  const openEditModal = async (it: InventoryItem) => {
    if (!practiceId) return;
    // Inventory Items is a management index; row click should navigate to the catalog page
    // and open the edit modal via URL params (back-button friendly).
    const go = (path: string) => navigate(path);
    if (it.catalog_type === 'product') {
      go(`/settings/products-catalog/${it.catalog_item_id}?edit=1`);
      return;
    }
    if (it.catalog_type === 'medication') {
      go(`/settings/medications-catalog/${it.catalog_item_id}?edit=1`);
      return;
    }
    if (it.catalog_type === 'immunization') {
      go(`/settings/immunization-catalog/${it.catalog_item_id}?edit=1`);
      return;
    }
    if (it.catalog_type === 'diagnostic') {
      go(`/settings/labs-catalog/${it.catalog_item_id}?edit=1`);
      return;
    }
    if (it.catalog_type === 'procedure') {
      go(`/settings/diagnostics-catalog/${it.catalog_item_id}?edit=1`);
      return;
    }
    // treatment
    go(`/settings/treatments-catalog/${it.catalog_item_id}?edit=1`);
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
        <div className="flex flex-col gap-4">
          {/* Search + actions */}
          <div className="flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
            <div className="relative flex-1 min-w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search inventory items…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Add Item dropdown (navigate to catalog) */}
            <div className="relative" ref={addMenuRef}>
              <button
                onClick={() => setShowAddMenu((v) => !v)}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium w-full md:w-auto"
              >
                <Plus className="w-4 h-4" />
                Add Item
              </button>
              {showAddMenu && (
                <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden">
                  <button
                    onClick={() => navigate('/settings/immunization-catalog?create=1')}
                    className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Immunization
                  </button>
                  <button
                    onClick={() => navigate('/settings/labs-catalog?create=1')}
                    className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Lab
                  </button>
                  <button
                    onClick={() => navigate('/settings/diagnostics-catalog?create=1')}
                    className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Diagnostic (Procedure)
                  </button>
                  <button
                    onClick={() => navigate('/settings/medications-catalog?create=1')}
                    className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Medication
                  </button>
                  <button
                    onClick={() => navigate('/settings/treatments-catalog?create=1')}
                    className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Treatment
                  </button>
                  <button
                    onClick={() => navigate('/settings/products-catalog?create=1')}
                    className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Product
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Filters (below search) */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2 min-w-0">
              {(['all', 'product', 'medication', 'immunization', 'diagnostic', 'procedure', 'treatment'] as CatalogTab[]).map(
                (t) => (
                  <button
                    key={t}
                    onClick={() => setActiveTab(t)}
                    className={`px-3 py-2 rounded-md text-sm font-medium border transition-colors ${
                      activeTab === t
                        ? 'bg-blue-50 text-blue-700 border-blue-200'
                        : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    {t === 'all'
                      ? 'All'
                      : t === 'diagnostic'
                      ? 'Labs'
                      : t === 'procedure'
                      ? 'Procedures'
                      : t === 'treatment'
                      ? 'Treatments'
                      : t[0].toUpperCase() + t.slice(1)}
                  </button>
                )
              )}
            </div>
          </div>

          {/* Billing-catalog style Filter button (below filters/search) */}
          <div className="relative" ref={filterMenuRef}>
            <button
              onClick={() => setShowFilterMenu((v) => !v)}
              className="p-2 rounded-lg border bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600 transition-colors"
              title="Filter options"
            >
              <Filter className="w-5 h-5" />
            </button>

            {showFilterMenu && (
              <div className="absolute top-full left-0 mt-2 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-50 min-w-56 overflow-hidden">
                <button
                  onClick={() => {
                    setStockTrackedOnlyPersisted(!stockTrackedOnly);
                    setShowFilterMenu(false);
                  }}
                  className="w-full px-4 py-3 text-left text-sm text-gray-200 hover:bg-gray-700"
                >
                  {stockTrackedOnly ? 'Show all items' : 'Show stock-tracked only'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Item</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Category</th>
              <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Selling Price</th>
              <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Stock-tracked</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && (
              <tr>
                <td colSpan={4} className="px-6 py-10 text-center text-sm text-gray-500">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={4} className="px-6 py-10 text-center text-sm text-gray-500">
                  No items found.
                </td>
              </tr>
            )}
            {!loading &&
              filtered.map((it) => (
                <tr
                  key={`${it.catalog_type}:${it.catalog_item_id}`}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => openEditModal(it)}
                >
                  <td className="px-6 py-4 text-sm text-gray-900">
                    <div className="font-medium">{it.name}</div>
                    {it.sku ? <div className="text-xs text-gray-500">SKU: {it.sku}</div> : null}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {it.catalog_type}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                    {it.unit_price ? `$${Number(it.unit_price).toFixed(2)}` : '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleStockTracked(it, !it.is_stock_tracked);
                      }}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        it.is_stock_tracked ? 'bg-green-500' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          it.is_stock_tracked ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};


